/* eslint-disable custom/no-hardcoded-colors */
import { useRum } from 'common-rum';
import { formatCurrency } from 'common-utils';
import { Button, rem, Spacer, styled, theme } from 'design-system';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  currency,
  getCountryCode,
  isUploadErrorPayload,
  localeList,
  localeListType,
} from 'preflight-core';
import React, { useEffect, useRef, useState } from 'react';

import { HeaderHomePageNew } from '../../../../../components/header-home-page-new';
import { AddOnExpert } from '../../../../../components/homepage/addon-expert-section';
import { AtWorkCardComponentNew } from '../../../../../components/homepage/at-work-card-new-component';
import { PeerPilotDescription } from '../../../../../components/homepage/peerpilot-description';
import { PeerPilotHeader } from '../../../../../components/homepage/peerpilot-header';
import { TrustComponent } from '../../../../../components/homepage/trust-component';
import { BlueBanner } from '../../../../../components/homepage/v2/blue-banner';
import { ChecklistPreview } from '../../../../../components/homepage/v2/checklist-preview';
import { Section1HomepageLHS } from '../../../../../components/homepage/v2/section1-homepage-lhs';
import { TakePreflightSection } from '../../../../../components/homepage/v2/take-preflight-section';
import { mapJournalToVariant } from '../../../../../components/journal-variant';
import { NudgingSlider } from '../../../../../components/nudging-slider';
import {
  Container,
  CustomFontHeaderContainer,
  TopContainer,
} from '../../../../../components/section';
import { useEventTracking } from '../../../../../contexts/tracking-context';
import { useScrollVisibility } from '../../../../../hooks/use-scroll-visibility';
import { HeroErrorNew } from '../../../../../partials/heros/hero-error-new';
import {
  Check,
  getJournal,
  Journal,
  JournalNotFoundError,
} from '../../../../../resources/journal';
import {
  getAllJournalSlugs,
  JournalSlugs,
} from '../../../../../resources/journal-slugs';
import { FooterInnerSection } from '../../../../../sections/footer';
import { TopBannersHome } from '../../../../../top-banners/top-banner-home';
import { VoucherBannerHome } from '../../../../../top-banners/voucher-banner-home';
import { mutateString } from '../../../../../utils/replace-language';
import { useUploadDocument } from '../../../../../utils/use-upload-document';
import { useVoucher } from '../../../../../vouchers/use-voucher';

type Props = {
  journalDetail: Journal;
};

const JournalInterstitialPage: NextPage<Props> = ({ journalDetail }) => {
  const [journal, setJournal] = useState<Journal>(journalDetail);
  const { trackEvent } = useEventTracking();
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['homepage']);
  const peerPilotSectionRef = useRef<HTMLDivElement>(null);
  const [errorSection, setErrorSection] = useState('');
  const [errorScreen, setErrorScreen] = useState(false);
  const [otherSectionClicked, setOtherSectionClicked] = useState(false);

  const [uploadError, setUploadError] = useState<{
    title?: string;
    message?: string;
  } | null>(null);

  // Store the upload trigger function from header
  const uploadTriggerRef = React.useRef<(() => void) | null>(null);

  // Track if upload was from header (to show modal) or first fold (to show inline error)
  const isHeaderUploadRef = React.useRef<boolean>(false);

  const isScrolledPastUpload = useScrollVisibility(scrollTargetRef);

  const router = useRouter();
  const voucher = useVoucher({
    publisherId: journal.publisher.slug,
    journalId: journal.slug,
  });
  const [defaultArticleTypeId, setDefaultArticleTypeId] = useState<
    number | undefined
  >();
  const [selectedArticleTypeId, setSelectedArticleTypeId] = useState<
    number | undefined
  >();
  const [selectedAddonId, setSelectedAddonId] = useState<string | undefined>();
  const handleScrollToPeerPilot = () => {
    if (peerPilotSectionRef.current) {
      const elementPosition =
        peerPilotSectionRef.current.getBoundingClientRect().top +
        window.pageYOffset;
      const offsetPosition = elementPosition - 150;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };
  const processedAddonData = React.useMemo(() => {
    if (
      journal.enabledAddOnServices &&
      journal.enabledAddOnServices.length > 0
    ) {
      return journal.enabledAddOnServices.map((addOnKey) => {
        const getAddonDisplayData = (addOnKey: string) => {
          switch (addOnKey) {
            case 'human_submission_support':
            case 'generic_human_submission_support':
              return {
                title: t(
                  'addon_expert.expert_submission_support',
                  'Expert Submission Support',
                ),
                description: t(
                  'addon_expert.descriptions.expert_submission',
                  'Support to format and submit your manuscript completely and correctly.',
                ),
                icon: '/ai-edited-main.svg',
              };
            case 'proofreading':
            case 'generic_human_proofreading':
              return {
                title: t(
                  'addon_expert.standard_proofreading_service',
                  'Standard Proofreading Service',
                ),
                description: t(
                  'addon_expert.descriptions.proofreading',
                  'Expert-led proofread to improve grammar, clarity, and consistency.',
                ),
                icon: '/expert.svg',
              };
            case 'art_work_formatting':
              return {
                title: t(
                  'addon_expert.artwork_formatting_service',
                  'Artwork Formatting',
                ),
                description: t(
                  'addon_expert.descriptions.artwork_formatting',
                  'Expert formatting of figures and tables to meet journal guidelines.',
                ),
                icon: '/proofreading.svg',
              };
            default:
              return {
                title: addOnKey || 'Professional Service',
                description: t(
                  'addon_expert.descriptions.default',
                  'Professional service to enhance your manuscript',
                ),
                icon: '/default-addon-icon.svg',
              };
          }
        };

        const displayData = getAddonDisplayData(addOnKey);

        return {
          id: addOnKey,
          title: displayData.title,
          description: displayData.description,
          icon: displayData.icon,
          price: 0,
          currency: 'USD',
        };
      });
    }

    return [];
  }, [journal.enabledAddOnServices]);

  const hasHumanServicesAddon = React.useMemo(() => {
    return (
      journal.enabledAddOnServices?.some(
        (addOnKey) =>
          addOnKey === 'human_proofreading' ||
          addOnKey === 'generic_human_proofreading' ||
          addOnKey === 'human_submission_support' ||
          addOnKey === 'generic_human_submission_support',
      ) ?? false
    );
  }, [journal.enabledAddOnServices]);

  const isAddonEnabledV3 = (journal.enabledAddOnServices?.length ?? 0) > 0;

  const { resetUpload, upload, uploadState, redirectToD } = useUploadDocument({
    demoId: journal.demoSubmissionId,
    journalId: journal.slug,
    publisherId: journal.publisher.slug,
    articleTypeId: selectedArticleTypeId || defaultArticleTypeId,
    selectedAddonId,
    isAddonEnabledV3,
  });

  useEffect(() => {
    if (uploadState.name === 'error' && otherSectionClicked === false) {
      setErrorScreen(true);
    }
  }, [uploadState]);

  const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState<
    string | undefined
  >(undefined);

  const [currentCountry, setCurrentCountry] = useState<string | undefined>();

  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    'USD',
  );
  const [localCurrencyEnabled, setLocalCurrencyEnabled] =
    useState<boolean>(false);

  const [price, setPrice] = useState<string | undefined>(
    formatCurrency(
      (journal.localPricing?.finalPrice
        ? journal.localPricing?.finalPrice
        : journal.salePriceCents) / 100,
      localeList['USD' as keyof localeListType],
      'USD',
    ),
  );

  useEffect(() => {
    const defaultArticleType = Array.isArray(journalDetail.articleTypes)
      ? journalDetail.articleTypes.find((type) => type.master)
      : undefined;
    if (defaultArticleType) {
      setDefaultArticleTypeId(defaultArticleType.id);
    }
  }, [journalDetail.articleTypes]);
  const fetchJournalDetails = async () => {
    try {
      const journalDetail = await getJournal({
        journalSlug: journal.slug,
        publisherSlug: journal.publisher.slug,
        currency: selectedPaymentCurrency,
        articleTypeId: selectedArticleTypeId?.toString(),
      });
      setJournal(journalDetail);
      setPrice(
        formatCurrency(
          (journalDetail.localPricing?.finalPrice
            ? journalDetail.localPricing.finalPrice
            : journalDetail.salePriceCents) / 100,
          localeList[selectedPaymentCurrency as keyof localeListType],
          selectedPaymentCurrency,
        ),
      );
      if (journalDetail.localPricing?.finalPrice) {
        setSelectedCurrency(selectedPaymentCurrency);
        setLocalCurrencyEnabled(true);
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (selectedPaymentCurrency && selectedPaymentCurrency !== 'USD') {
      fetchJournalDetails();
    }
  }, [selectedPaymentCurrency]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedArticleTypeId) {
      setLoading(true);
      fetchJournalDetails().finally(() => setLoading(false));
    }
  }, [selectedArticleTypeId]);
  useEffect(() => {
    const fetchClientAddress = async () => {
      try {
        const country = await getCountryCode();
        setCurrentCountry(country);
        if (country === 'CN') {
          setSelectedPaymentCurrency(currency.CNY);
        } else if (country === 'IN') {
          setSelectedPaymentCurrency(currency.INR);
        } else if (country === 'JP') {
          setSelectedPaymentCurrency(currency.JPY);
          // } else if (country === 'GB') {
          //   setSelectedPaymentCurrency(currency.GBP);
          // } else if (country === 'KR') {
          //   setSelectedPaymentCurrency(currency.KRW);
          // } else if (
          //   [
          //     'FR',
          //     'DE',
          //     'IT',
          //     'ES',
          //     'NL',
          //     'BE',
          //     'AT',
          //     'IE',
          //     'PT',
          //     'FI',
          //     'GR',
          //     'LU',
          //     'SI',
          //     'SK',
          //     'EE',
          //     'LV',
          //     'LT',
          //     'CY',
          //     'MT',
          //   ].includes(country)
          // ) {
          //   setSelectedPaymentCurrency(currency.EUR);
          // } else if (country === 'SG') {
          //   setSelectedPaymentCurrency(currency.SGD);
          // } else if (country === 'MY') {
          //   setSelectedPaymentCurrency(currency.MYR);
          // } else if (country === 'ID') {
          //   setSelectedPaymentCurrency(currency.IDR);
          // } else if (country === 'BR') {
          //   setSelectedPaymentCurrency(currency.BRL);
        } else {
          setSelectedPaymentCurrency(currency.USD);
        }
      } catch (error) {
        console.error('Failed to fetch client address:', error);
      }
    };

    fetchClientAddress();
  }, []);

  const [showQuickView, setShowQuickView] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const checklist = journal.checklist.checks;
  const langCheckCount = Object.values(checklist).filter(
    (item) => item.grouping === 'language',
  ).length;
  const techCheckCount = Object.values(checklist).filter(
    (item) => item.grouping !== 'language',
  ).length;

  const [fileSize, setFileSize] = useState<number>();

  function onFileSelected(file: File, addonId?: string): void {
    setFileSize(file.size);
    setSelectedAddonId(addonId); // Store the addon ID for later use
    isHeaderUploadRef.current = false; // Mark as first fold upload
    upload(file);
    setOtherSectionClicked(false);

    if (addonId) {
      trackEvent([
        'Addon upload intent',
        'Upload click',
        `Addon upload: ${addonId}`,
        {
          addonId,
          Section: 'Addon Section',
        },
      ]);
    }
  }

  // function onFileSelectedSection2(file: File): void {
  //   setFileSize(file.size);
  //   isHeaderUploadRef.current = false; // Mark as section upload
  //   upload(file);
  //   setOtherSectionClicked(false);
  // }

  function onFileSelectedSection3(file: File): void {
    setFileSize(file.size);
    isHeaderUploadRef.current = false; // Mark as section upload
    upload(file);
    setOtherSectionClicked(false);
  }

  function onFileSelectedFromHeader(file: File): void {
    setFileSize(file.size);
    setUploadError(null);
    isHeaderUploadRef.current = true; // Mark as header upload
    upload(file);
    trackEvent([
      'Manuscript upload intent',
      'Upload click',
      'Header Free Check button',
      {
        Section: 'Header',
      },
    ]);
  }

  const handleUploadErrorClose = () => {
    setUploadError(null);
  };

  const handleUploadRetry = () => {
    setUploadError(null);
    // Trigger the file upload dialog
    if (uploadTriggerRef.current) {
      uploadTriggerRef.current();
    }
  };

  const handleUploadTriggerReady = (trigger: () => void) => {
    uploadTriggerRef.current = trigger;
  };

  function onFileSelectedNudge(file: File): void {
    setFileSize(file.size);
    upload(file, true);
  }

  const setOpenQuickView = () => {
    setShowQuickView(false);
  };
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const rum = useRum();
  useEffect(() => {
    if (uploadState.name === 'success') {
      trackEvent([
        'Processing_page',
        'Upload_completed',
        'File upload completed',
      ]);
      if (uploadState.data.isCreatedFromDocx) {
        setShowQuickView(true);
      } else {
        redirectToD(uploadState.data);
      }
    }
    if (uploadState.name === 'error') {
      let errorMessage = isUploadErrorPayload(uploadState.error?.json)
        ? uploadState.error?.json?.errorReason ?? ''
        : JSON.stringify(uploadState.error.json) ?? '';

      const maxFileSize20MB = 20000000;

      if (fileSize && fileSize > maxFileSize20MB) {
        errorMessage = ` ${errorMessage}, File Size of uploaded document is ${fileSize} Bytes`;
      }

      // Only show modal for header uploads, first fold shows inline error
      if (isHeaderUploadRef.current) {
        setUploadError({
          title: 'Upload Failed',
          message:
            errorMessage ||
            'An error occurred while uploading your file. Please try again.',
        });
      }

      rum.pushError(new Error(errorMessage), {
        type: 'Submit page error',
        context: {
          errorName: 'File processing failed',
        },
      });
      trackEvent([
        'Report generation status',
        'Processing failed',
        'File processing failed',
        {
          ReportStatus: 'NA',
          GenerationStatus: 'failed to generate',
          ReasonForFailure: errorMessage,
          CountryCode: currentCountry ?? '',
        },
      ]);
    }
  }, [uploadState.name]);

  const showLogo =
    journal !== undefined &&
    journal.publisher.slug !== 'paperpal' &&
    journal.publisher.slug !== 'gjd-journal-finder' &&
    journal.publisher.slug !== 'editage-cn' &&
    journal.publisher.slug !== 'editage-us';
  const [languageCheckLists, setLanguageCheckLists] = useState<Check[]>([]);
  const [technicalCheckLists, setTechnicalCheckLists] = useState<Check[]>([]);
  useEffect(() => {
    const technicalChecks = Object.values(journal.checklist.checks).filter(
      (check) => check.grouping !== 'language',
    );
    setTechnicalCheckLists(technicalChecks); // Store the full check objects for technical checks

    const languageCheckList = Object.values(journal.checklist.checks).filter(
      (check) => check.grouping === 'language',
    );
    setLanguageCheckLists(languageCheckList); // Store the full check objects for language checks
  }, [journal.checklist]);

  const handleContinueWithPdfCallback = (newBool: boolean) => {
    RedirectNewPdf(newBool);
  };

  const RedirectNewPdf = (newBool: boolean) => {
    if (uploadState.name === 'success') {
      redirectToD(uploadState.data, newBool);
    }
  };

  const mappedJournal =
    journal && journal.publisher
      ? mapJournalToVariant(journal.publisher.slug)
      : undefined;
  const isEmeraldJournal =
    journal &&
    journal.publisher &&
    (journal.publisher.slug === 'emerald' ||
      journal.publisher.slug === 'emerald-publishing-services');

  return (
    <>
      {showQuickView && (
        <NudgingSlider
          uploadState={uploadState}
          onFileSelected={onFileSelectedNudge}
          open={showQuickView}
          setOpen={setOpenQuickView}
          onContinueWithPdfClick={handleContinueWithPdfCallback}
          // preflightNudgeAccepted={handleContinueWithWordCallback}
        />
      )}
      <TopContainer journal={mappedJournal}>
        <StickyHeader>
          <TopBannersHome
            submissionState={null}
            hasMacros={false}
            showServiceUnavaliable={false}
            journal={journal}
            voucher={voucher.data ? voucher.data : null}
          />
          <div ref={headerRef}>
            <HeaderHomePageNew
              journal={journal}
              onLogoClick={resetUpload}
              showFreeCheckButton={isScrolledPastUpload}
              onFileSelected={onFileSelectedFromHeader}
              uploadError={uploadError}
              onUploadErrorClose={handleUploadErrorClose}
              onUploadRetry={handleUploadRetry}
              uploading={
                uploadState.name === 'loading' || uploadState.name === 'success'
              }
              onUploadTriggerReady={handleUploadTriggerReady}
            />
          </div>
        </StickyHeader>
        {journal.peerPilotEnabled && (
          <SectionNewV1 bgTheme="green" size="sm2">
            <PeerPilotHeader onClick={handleScrollToPeerPilot} />
          </SectionNewV1>
        )}
        <SectionNewV1
          size="sm"
          style={{ paddingTop: '5px', paddingBottom: '0px' }}
        >
          <Container data-test-id="voucher-banner">
            {voucher.data ? (
              <div>
                <VoucherText>
                  {voucher.data ? (
                    <VoucherBannerHome voucher={voucher.data} />
                  ) : undefined}
                </VoucherText>
                <Spacer height={32} />
              </div>
            ) : (
              ''
            )}{' '}
            <FullWidthWrapper
              ref={scrollTargetRef}
              style={{ display: 'flex', gap: '9px' }}
            >
              {errorSection === 'Section_1' && errorScreen && !showQuickView ? (
                <LeftReadyToSubmitSection>
                  <HeroErrorNew
                    onFileSelected={onFileSelected}
                    uploadState={uploadState}
                    showSubmission={false}
                    submissionUrl={''}
                  />
                </LeftReadyToSubmitSection>
              ) : (
                <Section1HomepageLHS
                  journal={journal}
                  loading={loading}
                  showLogo={showLogo}
                  languageCheckLists={languageCheckLists}
                  technicalCheckLists={technicalCheckLists}
                  setSelectedArticleTypeId={setSelectedArticleTypeId}
                  onFileSelected={onFileSelected}
                  uploadState={uploadState}
                  showQuickView={showQuickView}
                  setErrorScreen={setErrorScreen}
                  errorSection={errorSection}
                  setErrorSection={setErrorSection}
                  setOtherSectionClicked={setOtherSectionClicked}
                  techCheckCount={techCheckCount}
                  langCheckCount={langCheckCount}
                  submitPage={true}
                  showFullJournalName={isEmeraldJournal}
                />
              )}
              <RightGradientSection
                style={{
                  marginTop:
                    journal.publisher.slug === 'gjd-journal-finder' ||
                    journal.publisher.slug === 'editage-cn'
                      ? '0px'
                      : '24px',
                }}
              >
                <RightReadyToSubmitSection style={{ flex: 1 }}>
                  <div
                    style={{
                      position: 'relative',

                      zIndex: 1,
                      overflow: 'hidden',
                      maxHeight:
                        journal.articleTypes && journal.articleTypes.length > 1
                          ? '613px'
                          : '500px',
                      textAlign: 'right',
                    }}
                  >
                    {/* {journal.publisher.slug === 'gjd-journal-finder' ||
                    journal.publisher.slug === 'editage-cn' ? (
                      <Image
                        width={590}
                        height={436}
                        src="/assets/v2-section1-gjd-manuscript-status.svg"
                        alt={journal.label}
                        priority
                        style={{ marginTop: '60px' }}
                      />
                    ) : (
                      <> */}
                    <Image
                      width={662}
                      height={0}
                      src={`/v1-landing-page/${router.locale}/hero-image-section.svg`}
                      style={{ display: 'inline-block' }}
                      alt={journal.label}
                    />
                    {isEmeraldJournal && journal.logo && (
                      <Image
                        width={300}
                        height={0}
                        src={journal.logo ? journal.logo : ' '}
                        alt="Emerald Publishing"
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '35px',
                          height: 'auto',
                          border: '2px solid rgba(20, 93, 198, 0.55)',
                          boxShadow: '0 10px 24px rgba(20, 93, 198, 0.15)',
                          borderRadius: '8px',
                        }}
                      />
                    )}
                    {/* <VideoComponent onClick={() => setShowVideo(true)}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        style={{ fill: 'currentColor' }}
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      <VideoCompText>
                        {t(
                          'section1_v2.watch_how_it_works',
                          'Watch how it works',
                        )}
                      </VideoCompText>
                    </VideoComponent> */}
                    {/* </>
                    )} */}
                  </div>
                  {showVideo &&
                    journal.publisher.slug !== 'gjd-journal-finder' &&
                    journal.publisher.slug !== 'editage-cn' && (
                      <>
                        <VideoComp>
                          <ShowVideo>
                            <ShowVideoIframe
                              style={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                              }}
                            >
                              <iframe
                                width="100%"
                                height="100%"
                                src="https://cdn.paperpal.com/assets/videos/preflight/iop-lp-v1.mp4"
                                style={{ border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />

                              <VideoButton onClick={() => setShowVideo(false)}>
                                <Image
                                  width={24}
                                  height={24}
                                  src={'/white-tick.svg'}
                                  alt={'cross-button'}
                                />
                              </VideoButton>
                            </ShowVideoIframe>
                          </ShowVideo>
                        </VideoComp>
                      </>
                    )}
                </RightReadyToSubmitSection>
              </RightGradientSection>
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>
        <SectionNewV1
          bgTheme="darkBlue"
          size={'xsm'}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Container>
            <FullWidthWrapper>
              <BlueBanner />
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>
        <SectionNewV1 bgTheme="white" size={'xsm2'}>
          <Container>
            <FullWidthWrapper>
              <RightGradientSection2>
                <TakePreflightSection
                  price={price}
                  selectedPaymentCurrency={selectedCurrency}
                  localCurrencyEnabled={localCurrencyEnabled}
                  langCheckCount={langCheckCount}
                  techCheckCount={techCheckCount}
                  journal={journal}
                />
              </RightGradientSection2>
            </FullWidthWrapper>
          </Container>
        </SectionNewV1>
        <SectionNewV1 bgTheme="lightBlue1">
          <Container>
            <FullWidthWrapper
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 1,
              }}
            >
              <SectionTitle
                style={{
                  textAlign: 'center',
                  color: theme.colors.blue10.value,
                  marginBottom: '10px',
                }}
              >
                {t('section2_v1.submission_readiness_checklist')}
              </SectionTitle>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeading
                  style={{
                    textAlign: 'center',
                    width: '591px',
                    marginBottom: '50px',
                  }}
                >
                  {showLogo
                    ? journal.publisher.slug === 'paperpal-preflight'
                      ? t('section2_v1.main_heading_pp')
                      : t('section2_v1.main_heading1')
                    : t('section2_v1.main_heading2')}
                </MainHeading>
              </CustomFontHeaderContainer>
              <Spacer height={30} />
            </FullWidthWrapper>
            <ChecklistPreview journal={journal} />
            <Spacer height={40} />
          </Container>
          <Spacer height={10} />
        </SectionNewV1>
        {journal.peerPilotEnabled && (
          <SectionNewV1
            bgTheme="bluegradient"
            size="xsm"
            ref={peerPilotSectionRef}
          >
            <Container>
              <PeerPilotDescription price={price} />
            </Container>
          </SectionNewV1>
        )}
        <SectionNewV1 bgTheme="white" style={{ marginBottom: -50 }}>
          <Container>
            <AtWork>
              <AtWorkLeft>
                {errorSection === 'Section_3' && errorScreen ? (
                  <LeftReadyToSubmitSectionError style={{ flex: '0 0 auto' }}>
                    <HeroErrorNew
                      onFileSelected={onFileSelectedSection3}
                      uploadState={uploadState}
                    />
                  </LeftReadyToSubmitSectionError>
                ) : (
                  <>
                    <SectionTitle style={{ justifyContent: 'left' }}>
                      {t('section4_v1.preflight_at_work')}
                    </SectionTitle>
                    <CustomFontHeaderContainer journal={mappedJournal}>
                      <MainHeading
                        style={{ marginBottom: '15px', marginTop: '4px' }}
                      >
                        {t('section4_v1.preflight_ready_to_submit')}
                      </MainHeading>
                    </CustomFontHeaderContainer>
                    <div
                      style={{
                        fontSize: '20px',
                        color: 'rgba(5, 30, 87, 1)',
                        lineHeight: '32px',
                        fontWeight: 400,
                      }}
                    >
                      {showLogo
                        ? mutateString(
                            journal.publisher.slug === 'aipp'
                              ? t('section4_v1.left_description_aipp')
                              : journal.publisher.slug === 'paperpal-preflight'
                              ? t('section4_v1.left_description_pp')
                              : t('section4_v1.left_description'),
                            { '{journalName}': journal.label },
                          )
                        : mutateString(t('section4_v1.left_description_else'), {
                            '{techCheckCount}': `${techCheckCount}`,
                            '{langCheckCount}': `${langCheckCount}`,
                          })}
                    </div>

                    <div style={{ paddingLeft: '50px', paddingTop: '30px' }}>
                      <Image
                        src="/v1-landing-page/section4-smile.svg"
                        alt="At Work"
                        style={{ marginTop: '20px' }}
                        width={220}
                        height={40}
                      />
                    </div>
                  </>
                )}
              </AtWorkLeft>
              <AtWorkRight>
                <AtWorkCardComponentNew
                  langCheckCount={langCheckCount}
                  techCheckCount={techCheckCount}
                  hasHumanServicesAddon={hasHumanServicesAddon}
                  journal={journal}
                  isPeerPilotEnabled={journal.peerPilotEnabled}
                  aiEditingPrice={formatCurrency(
                    (journal.localPricing?.finalPrice ??
                      journal.salePriceCents ??
                      2900) / 100,
                    localeList[
                      (selectedPaymentCurrency ??
                        selectedCurrency ??
                        'USD') as keyof localeListType
                    ],
                    selectedPaymentCurrency ?? selectedCurrency ?? 'USD',
                  )}
                />
              </AtWorkRight>
            </AtWork>
          </Container>
        </SectionNewV1>
        {processedAddonData && processedAddonData.length > 0 && (
          <SectionNewV1
            bgTheme="lightBlue1"
            style={{ paddingTop: '10px', paddingBottom: '10px' }}
          >
            <Container>
              <AddOnExpert
                addons={processedAddonData}
                onFileSelected={onFileSelected}
                uploadState={uploadState}
                setErrorSection={setErrorSection}
                setOtherSectionClicked={setOtherSectionClicked}
                errorSection={errorSection}
                errorScreen={errorScreen}
              />
            </Container>
          </SectionNewV1>
        )}
        <SectionNewV1 bgTheme="darkBlue">
          <RightGradientSection3>
            <Container>
              <MainHeadingCommitment2
                style={{
                  marginBottom: '4px',
                  textAlign: 'center',
                }}
              >
                {t('section5_V1.our_commitment', 'OUR COMMITMENT')}
                {''}
              </MainHeadingCommitment2>
              <CustomFontHeaderContainer journal={mappedJournal}>
                <MainHeadingCommitment
                  style={{
                    marginBottom: '40px',
                    textAlign: 'center',
                    color: 'white',
                  }}
                >
                  {t('section5_V1.why_trust_paperpal')}
                </MainHeadingCommitment>
              </CustomFontHeaderContainer>
              <TrustContainer>
                <TrustComponent v1={true} />
              </TrustContainer>
              <div
                style={{
                  marginTop: '20px',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                {' '}
              </div>
            </Container>
          </RightGradientSection3>
        </SectionNewV1>

        <SectionNewV1
          bgTheme="grayDark"
          style={{ paddingTop: '0px', paddingBottom: '0px' }}
        >
          <FooterInnerSection journal={journal} />
        </SectionNewV1>
      </TopContainer>
    </>
  );
};

export default JournalInterstitialPage;

export const getStaticPaths: GetStaticPaths<JournalSlugs> = async () => {
  const allJournalSlugs = await getAllJournalSlugs();
  const paths = allJournalSlugs.map(({ journalSlug, publisherSlug }) => ({
    params: {
      journalSlug,
      publisherSlug,
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props, JournalSlugs> = async (
  context,
) => {
  const journalSlugs = context.params;
  const { locale } = context;

  if (!journalSlugs) {
    console.error('Unable to get static props without journal slugs');
    return { notFound: true };
  }

  if (
    journalSlugs.publisherSlug === 'iop' &&
    journalSlugs.journalSlug === 'ERC'
  ) {
    return {
      redirect: {
        destination: '/partner/iop/enviro-research-comm/submit',
        permanent: true,
      },
    };
  }

  let journalDetail: Journal;

  try {
    journalDetail = await getJournal(journalSlugs);
  } catch (error) {
    if (JournalNotFoundError.safeParse(error).success) {
      return { notFound: true };
    }

    throw error;
  }

  let translations = {};

  if (locale) {
    translations = await serverSideTranslations(locale, [
      'homepage',
      'common',
      'error',
    ]);
  }

  return {
    props: { journalDetail, ...translations },
    revalidate: 5,
  };
};

const StickyHeader = styled('div', {
  position: 'sticky',
  top: 0,
  zIndex: 2,
});

const VoucherText = styled('div', {
  fontSize: '18px',
  color: theme.colors.blue11,
  fontWeight: 700,
  lineHeight: '23.4px',
  borderRadius: '10px',
});

const FullWidthWrapper = styled('div', {
  width: '100%',
  marginBottom: -40,
});

const TrustContainer = styled('div', {
  display: 'flex',
});

const AtWork = styled('div', {
  display: 'flex',
  gap: '72px',
});

const AtWorkLeft = styled('div', {
  width: '450px',
});

const AtWorkRight = styled('div', {
  flex: 1,
});

const MainHeadingCommitment = styled('span', {
  fontSize: '28px',
  lineHeight: '42px',
  fontWeight: '700',
  display: 'block',
  color: theme.colors.blue12,
});

const MainHeadingCommitment2 = styled('span', {
  fontSize: '20px',

  fontWeight: '700',
  display: 'block',
  color: 'rgba(94, 150, 239, 1)',
});

export const SectionTitle = styled('span', {
  display: 'flex',
  fontSize: '20px',
  color: theme.colors.blue11,
  fontWeight: 700,
  justifyContent: 'center',
  justifyItems: 'center',
  textTransform: 'uppercase',
});

export const MainHeading = styled('span', {
  fontSize: '24px',
  lineHeight: '42px',
  fontWeight: '600',
  display: 'block',
  color: 'rgba(5, 30, 87, 1)',
  // textTransform: 'capitalize',
});

const LeftReadyToSubmitSection = styled('div', {
  flex: 1,
  paddingTop: '40px',
});
const LeftReadyToSubmitSectionError = styled('div', {
  flex: 1,
});
const RightReadyToSubmitSection = styled('div', {
  width: '550px',
});

const ShowVideo = styled('div', {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '756px',
  height: '425px',
  backgroundColor: theme.colors.black,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  borderRadius: '10px',
  boxShadow: '0px 12.14px 24.29px 0px #23170542',
});
const ShowVideoIframe = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
});

export const SectionNewV1 = styled('div', {
  paddingTop: rem(48),
  paddingBottom: rem(80),
  background: theme.colors.white,
  paddingLeft: rem(32),
  paddingRight: rem(32),
  minWidth: rem(1234),
  variants: {
    size: {
      xsm: {
        paddingTop: '30px',
        paddingBottom: '20px',
      },
      xsm2: {
        paddingTop: '30px',
        paddingBottom: '30px',
      },
      sm: {
        paddingTop: rem(40),
        paddingBottom: rem(40),
      },
      md: {
        paddingTop: rem(60),
        paddingBottom: rem(60),
      },
      lg: {
        paddingTop: rem(70),
        paddingBottom: rem(70),
      },
      sm2: {
        maxHeight: '36px',
        paddingTop: '6px',
        paddingBottom: '12px',
      },
    },
    bgTheme: {
      gray: {
        background: theme.colors.gray2,
      },
      grayDark: {
        background: theme.colors.gray5,
      },
      yellow: {
        background:
          'linear-gradient(to top, rgba(249, 240, 209, 1), rgba(249, 240, 209, 0.5), rgba(249, 240, 209, 0))',
      },
      darkBlue: {
        background: 'rgba(5, 30, 87, 1)',
      },
      lightBlue1: {
        background: 'rgba(237, 243, 255, 1)',
      },
      lightBlue2: {
        background: 'rgba(225, 235, 255, 1)',
      },
      white: {
        background: 'rgba(255, 255, 255, 1)',
      },
      green: {
        background: 'linear-gradient(90deg, #1E5431 0%, #0A8734 100%)',
      },
      bluegradient: {
        background:
          'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 50%, #EEF2FF 100%)',
      },
    },
  },
});

// const VideoComponent = styled('div', {
//   position: 'absolute',
//   top: '50%',
//   left: '50%',
//   height: '42px',
//   transform: 'translate(-50%, -50%)',
//   backgroundColor: theme.colors.white,
//   color: theme.colors.blue12,
//   borderRadius: 40,
//   padding: '10px',
//   paddingLeft: '20px',
//   paddingRight: '20px',
//   zIndex: 1,
//   display: 'flex',
//   alignItems: 'center',
//   gap: '8px',
//   cursor: 'pointer',
//   '&::before': {
//     content: '""',
//     position: 'absolute',
//     inset: 0,
//     borderRadius: 40,
//     padding: '1.44px',
//     background: 'linear-gradient(90deg, #4AD82A 0%, #0054F1 100%)',
//     WebkitMask:
//       'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
//     WebkitMaskComposite: 'xor',
//     maskComposite: 'exclude',
//     pointerEvents: 'none',
//   },

//   '&:hover::before': {
//     background: 'linear-gradient(90deg, #0054F1 0%, #4AD82A 100%)',
//   },

//   '&:hover': {
//     backgroundColor: theme.colors.blue10,
//     color: theme.colors.white,
//   },
// });

// const VideoCompText = withOnClickTracking(
//   styled('div', {
//     fontWeight: 600,
//     fontSize: '14px',
//     lineHeight: '20px',
//     width: '132px',
//   }),
// );
const VideoButton = styled(Button, {
  position: 'absolute',
  top: '-50px',
  right: '-50px',
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
});

const VideoComp = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9998,
  backdropFilter: 'blur(7px)',
});
const RightGradientSection = styled('div', {
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '40px',
    left: '40px',
    right: '40px',
    bottom: '40px',
    background:
      'linear-gradient(rgb(255 216 221) 0%, rgb(236, 193, 79) 40.5%, hsl(140deg 67.72% 82.55%) 84%, rgb(6 139 222) 100%)',
    opacity: '20%',
    zIndex: 0,
    transform: 'rotate(-280deg)',
    pointerEvents: 'none',
    filter: 'blur(70px)',
  },
});
const RightGradientSection2 = styled('div', {
  position: 'relative',
  display: 'flex',
  marginTop: '14px',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0px 0px 0px 700px',
    background: 'linear-gradient(rgb(255 164 175) 20%, rgb(197 233 255) 80%)',
    opacity: '0.2',
    zIndex: 0,
    transform: 'rotate(-242deg)',
    pointerEvents: 'none',
    filter: 'blur(80px)',
    width: '400px',
  },
});
const RightGradientSection3 = styled('div', {
  position: 'relative',
  display: 'flex',
  marginTop: '14px',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0px 0px 0px auto',
    background: 'linear-gradient(rgb(255 18 45) 20%, rgb(20 165 255) 80%)',
    opacity: '0.2',
    zIndex: 0,
    transform: 'rotate(-243deg)',
    pointerEvents: 'none',
    filter: 'blur(80px)',
    width: '447px',
    marginRight: '189px',
  },
});
